import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { clearInventering } from 'store/aktivitetSlice';
import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaDialog } from 'raa-react';

import { InventeringPreliminar } from 'models';

import { useDeleteAktivitetPreliminarMutation } from 'api/endpoints/aktivitetPreliminarSlice';

interface DeleteInventeringDialogProps {
  inventeringPreliminar?: InventeringPreliminar;
  unsetDialog: () => void;
  setDeleteSuccessful: Dispatch<SetStateAction<boolean>>;
  setDeleteLoading: Dispatch<SetStateAction<boolean>>;
}

const DeleteInventeringPreliminarDialog: FC<DeleteInventeringDialogProps> = ({
  inventeringPreliminar,
  unsetDialog,
  setDeleteSuccessful,
  setDeleteLoading
}): ReactElement => {
  const [error, setError] = useState<string>();
  const [headerError, setHeaderError] = useState<string>();
  const navigate = useNavigate();
  const bebyggelseobjektUtkast = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast.data);

  const [deleteAktivitetPreliminar, deleteInventeringResults] = useDeleteAktivitetPreliminarMutation();
  const { isLoading: deleteAktivitetIsLoading, isSuccess: deleteAktivitetPreliminarIsSuccess } =
    deleteInventeringResults;

  const deleteAktivitetPreliminarIsSuccessRef = useRef<boolean>();
  deleteAktivitetPreliminarIsSuccessRef.current = deleteAktivitetPreliminarIsSuccess;

  useEffect(() => {
    setDeleteLoading(deleteAktivitetIsLoading);
  }, [setDeleteLoading, deleteAktivitetIsLoading]);

  useEffect(() => {
    setDeleteSuccessful(deleteAktivitetPreliminarIsSuccess);
  }, [setDeleteSuccessful, deleteAktivitetPreliminarIsSuccess]);

  const handleDelete = async (inventeringPreliminar?: InventeringPreliminar): Promise<void> => {
    setError('');

    if (!inventeringPreliminar) {
      clearInventering();
      navigate('/', { replace: true });
      unsetDialog();
    } else {
      if (bebyggelseobjektUtkast && bebyggelseobjektUtkast.length) {
        setError('Ta bort alla utkast för inventerade objekt och försök igen.');
        setHeaderError('Inventeringen kan inte raderas');
        return;
      }

      await deleteAktivitetPreliminar({
        aktivitetId: inventeringPreliminar.aktivitet_id,
        eTag: inventeringPreliminar.eTag
      });

      if (deleteAktivitetPreliminarIsSuccessRef.current) {
        navigate('/', { replace: true });
        unsetDialog();
      }
    }
  };

  return (
    <RaaDialog
      cancel={unsetDialog}
      proceed={error ? undefined : () => handleDelete(inventeringPreliminar)}
      close={unsetDialog}
      header={headerError ? headerError : error ? 'Något gick fel!' : 'Vill du radera inventeringen?'}
      proceedButtonText="radera"
      cancelButtonText={error ? 'stäng' : 'avbryt'}
      showLoadingSpinner={deleteAktivitetIsLoading}
      ariaDescribedBy="dialog-description"
    >
      {error && (
        <p id="dialog-description" className="tw-mt-8">
          {error}
        </p>
      )}
    </RaaDialog>
  );
};

export default DeleteInventeringPreliminarDialog;
